import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthUser } from '@ventin/shared';
import { apiFetch } from '@/lib/api';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  impersonatedBy: string | null;
  role: string | null;
  companyId: string | null;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, password: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | undefined>(undefined);

async function loadMe(): Promise<AuthUser | null> {
  try {
    const res = await apiFetch<{ user: AuthUser | null }>('/api/auth/me');
    return res.user;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const me = await loadMe();
    setUser(me);
  }

  useEffect(() => {
    let alive = true;
    loadMe()
      .then((me) => {
        if (alive) setUser(me);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      impersonatedBy: user?.impersonatedBy ?? null,
      role: user?.role ?? null,
      companyId: user?.companyId ?? null,
      signIn: async (email, password) => {
        const res = await apiFetch<{ user: AuthUser }>('/api/auth/login', {
          method: 'POST',
          body: { email, password },
        });
        setUser(res.user);
        return res.user;
      },
      signOut: async () => {
        await apiFetch<void>('/api/auth/logout', { method: 'POST' });
        setUser(null);
      },
      resetPassword: async (email) => {
        await apiFetch<void>('/api/auth/password-reset/request', {
          method: 'POST',
          body: { email },
        });
      },
      confirmPasswordReset: async (token, password) => {
        await apiFetch<void>('/api/auth/password-reset/confirm', {
          method: 'POST',
          body: { token, password },
        });
      },
      refresh,
    }),
    [user, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
